/* eslint-disable react/prop-types */

import {
    Box,
    Button,
    Chip,
    IconButton,
    LinearProgress,
    Paper,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type Employee from '@src/Objects/Employee';
import type { AppState } from '@store/store';
import {
    ArrowRight,
    Award,
    CheckCircle,
    Clock,
    Columns2,
    Plus,
    Rocket,
    Rows2,
    Star,
    Target,
    Trophy,
    Users,
    Zap,
} from 'lucide-react';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import T4loAssistantInfoBox from './components/T4loAssistantInfoBox';

interface T4loMissionsTabProps {
    employee: Employee;
}

interface Mission {
    id: number;
    type: string;
    title: string;
    description: string;
    progress: number;
    reward: string;
    points: number;
    icon: React.ReactElement;
    color: string;
}

interface CompletedMission {
    id: number;
    title: string;
    reward: string;
    points: number;
    completedAt: string;
}

function T4loMissionsTab({ employee }: T4loMissionsTabProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const featureFlags = useSelector((state: AppState) => state.company.featureFlags);
    const gameEnabled = featureFlags?.game || false;

    const [viewMode, setViewMode] = useState<'2-column' | '1-column'>('2-column');
    const currentLayout = isMobile ? '1-column' : viewMode;

    // Mock data for missions with points
    const activeMissions = [
        {
            id: 1,
            type: 'level-up',
            title: 'Python Data Science Mastery',
            description:
                'Entwickle 3 Machine Learning Modelle mit echten Unternehmensdaten. Mentoring durch Senior Data Scientists inklusive.',
            progress: 42,
            reward: '🎖️ Data Science Expert',
            points: 250,
            icon: <Target size={16} />,
            color: theme.palette.primary.main,
        },
        {
            id: 2,
            type: 'boost',
            title: 'AI Integration Specialist',
            description:
                'Implementiere ChatGPT API in 2 bestehende Projekte. Werde zum AI-Champion deines Teams mit praktischen Workshops.',
            progress: 15,
            reward: '🤖 AI Pioneer Badge',
            points: 180,
            icon: <Rocket size={16} />,
            color: theme.palette.secondary.main,
        },
    ];

    const completedMissions = [
        {
            id: 3,
            title: 'React Hooks Master',
            reward: '⚛️ React Expert',
            points: 150,
            completedAt: '2024-01-15',
        },
        {
            id: 4,
            title: 'TypeScript Pro',
            reward: '🔷 TypeScript Guru',
            points: 120,
            completedAt: '2024-01-10',
        },
    ];

    // Mock user stats
    const userStats = {
        totalPoints: 1250,
        level: 8,
        badges: 12,
        activeMissions: activeMissions.length,
        completedMissions: completedMissions.length,
    };

    const StatsCards = () => {
        const tiles = [
            {
                title: 'Level',
                value: userStats.level.toString(),
                icon: <Star size={32} strokeWidth={1.5} />,
                color: '#ffb74d',
                bgColor: alpha('#ffb74d', 0.1),
                borderColor: alpha('#ffb74d', 0.3),
            },
            {
                title: 'Punkte',
                value: userStats.totalPoints.toString(),
                icon: <Zap size={32} strokeWidth={1.5} />,
                color: theme.palette.primary.main,
                bgColor: alpha(theme.palette.primary.main, 0.1),
                borderColor: alpha(theme.palette.primary.main, 0.3),
            },
            {
                title: 'Badges',
                value: userStats.badges.toString(),
                icon: <Award size={32} strokeWidth={1.5} />,
                color: theme.palette.secondary.main,
                bgColor: alpha(theme.palette.secondary.main, 0.1),
                borderColor: alpha(theme.palette.secondary.main, 0.3),
            },
            {
                title: 'Aktive Missionen',
                value: userStats.activeMissions.toString(),
                icon: <Target size={32} strokeWidth={1.5} />,
                color: theme.palette.info.main,
                bgColor: alpha(theme.palette.info.main, 0.1),
                borderColor: alpha(theme.palette.info.main, 0.3),
            },
            {
                title: 'Abgeschlossen',
                value: userStats.completedMissions.toString(),
                icon: <CheckCircle size={32} strokeWidth={1.5} />,
                color: theme.palette.success.main,
                bgColor: alpha(theme.palette.success.main, 0.1),
                borderColor: alpha(theme.palette.success.main, 0.3),
            },
        ];

        const visibleTiles = gameEnabled ? tiles : tiles.slice(3); // Show only mission tiles if game disabled

        return (
            <Box>
                {/* Desktop Grid */}
                <Box
                    sx={{
                        display: { xs: 'none', md: 'grid' },
                        gridTemplateColumns: `repeat(${Math.min(visibleTiles.length, 3)}, 1fr)`,
                        gap: 1.5,
                        mb: 3,
                    }}
                >
                    {visibleTiles.map((tile, index) => (
                        <Box
                            key={index}
                            sx={{
                                p: 1.5,
                                borderRadius: '16px 2px',
                                backgroundColor: tile.bgColor,
                                border: `1px solid ${tile.borderColor}`,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                textAlign: 'center',
                                minHeight: 80,
                            }}
                        >
                            <Box sx={{ color: tile.color, mb: 0.5 }}>{React.cloneElement(tile.icon, { size: 24 })}</Box>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    color: theme.palette.text.primary,
                                    mb: 0.25,
                                    fontSize: '1.1rem',
                                }}
                            >
                                {tile.value}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ color: theme.palette.text.secondary, fontWeight: 500, fontSize: '0.7rem' }}
                            >
                                {tile.title}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Mobile Horizontal Scroll */}
                <Box
                    sx={{
                        display: { xs: 'flex', md: 'none' },
                        overflowX: 'auto',
                        gap: 2,
                        mb: 3,
                        pb: 1,
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollbarWidth: 'none',
                    }}
                >
                    {visibleTiles.map((tile, index) => (
                        <Box
                            key={index}
                            sx={{
                                p: 2,
                                borderRadius: 4,
                                backgroundColor: tile.bgColor,
                                border: `2px solid ${tile.borderColor}`,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                textAlign: 'center',
                                minHeight: 100,
                                minWidth: 120,
                                flexShrink: 0,
                            }}
                        >
                            <Box sx={{ color: tile.color, mb: 1 }}>{tile.icon}</Box>
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 0.5 }}
                            >
                                {tile.value}
                            </Typography>
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                                {tile.title}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    };

    const MissionCard = ({ mission }: { mission: Mission }) => (
        <Box
            sx={{
                border: `1px solid ${alpha(mission.color, 0.3)}`,
                borderRadius: 2,
                p: 2,
                bgcolor: alpha(mission.color, 0.08),
                mb: 2,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ color: mission.color }}>{mission.icon}</Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                    {mission.title}
                </Typography>
                {gameEnabled && (
                    <Chip
                        label={`${mission.points} XP`}
                        size="small"
                        sx={{
                            bgcolor: alpha('#ffb74d', 0.2),
                            color: '#ffb74d',
                            fontSize: '0.7rem',
                            mr: 1,
                            fontWeight: 600,
                            border: `1px solid ${alpha('#ffb74d', 0.4)}`,
                            boxShadow: `0 0 8px ${alpha('#ffb74d', 0.3)}`,
                            animation: 'pulse 2s infinite',
                            '@keyframes pulse': {
                                '0%': { boxShadow: `0 0 8px ${alpha('#ffb74d', 0.3)}` },
                                '50%': { boxShadow: `0 0 12px ${alpha('#ffb74d', 0.5)}` },
                                '100%': { boxShadow: `0 0 8px ${alpha('#ffb74d', 0.3)}` },
                            },
                        }}
                    />
                )}
                <Chip
                    label={mission.reward}
                    size="small"
                    sx={{
                        bgcolor: alpha(mission.color, 0.2),
                        color: mission.color,
                        fontSize: '0.7rem',
                    }}
                />
            </Box>

            <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
                {mission.description}
            </Typography>

            {mission.progress > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption">Fortschritt</Typography>
                        <Typography variant="caption">{mission.progress}%</Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={mission.progress}
                        sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: alpha(theme.palette.divider, 0.2),
                            '& .MuiLinearProgress-bar': {
                                bgcolor: mission.color,
                                borderRadius: 3,
                            },
                        }}
                    />
                </Box>
            )}

            <Button
                variant="contained"
                color={mission.color === theme.palette.primary.main ? 'primary' : 'secondary'}
                size="small"
                endIcon={<ArrowRight size={14} />}
                onClick={() => console.log(`Mission ${mission.type} gestartet`)}
                sx={{
                    textTransform: 'none',
                    fontSize: '0.8rem',
                }}
            >
                {mission.progress > 0 ? 'Fortsetzen' : 'Starten'}
            </Button>
        </Box>
    );

    const CompletedMissionCard = ({ mission }: { mission: CompletedMission }) => (
        <Box
            sx={{
                border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                borderRadius: 2,
                p: 2,
                bgcolor: alpha(theme.palette.success.main, 0.05),
                mb: 2,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CheckCircle size={16} color={theme.palette.success.main} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                    {mission.title}
                </Typography>
                {gameEnabled && (
                    <Chip
                        label={`+${mission.points} XP`}
                        size="small"
                        sx={{
                            bgcolor: alpha(theme.palette.success.main, 0.2),
                            color: theme.palette.success.main,
                            fontSize: '0.7rem',
                            mr: 1,
                            fontWeight: 600,
                            border: `1px solid ${alpha(theme.palette.success.main, 0.4)}`,
                            boxShadow: `0 0 6px ${alpha(theme.palette.success.main, 0.4)}`,
                            '&::before': {
                                content: '"✨"',
                                mr: 0.5,
                            },
                        }}
                    />
                )}
                <Chip
                    label={mission.reward}
                    size="small"
                    sx={{
                        bgcolor: alpha(theme.palette.success.main, 0.2),
                        color: theme.palette.success.main,
                        fontSize: '0.7rem',
                    }}
                />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Clock size={12} color={theme.palette.text.secondary} />
                <Typography variant="caption" color="text.secondary">
                    Abgeschlossen am {new Date(mission.completedAt).toLocaleDateString()}
                </Typography>
            </Box>
        </Box>
    );

    const MissionsContent = () => (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                bgcolor: 'background.paper',
            }}
        >
            <Stack spacing={3}>
                {/* Active Missions */}
                <Box>
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                        <Target size={20} color={theme.palette.primary.main} />
                        Aktive Missionen ({activeMissions.length})
                    </Typography>
                    {activeMissions.map((mission) => (
                        <MissionCard key={mission.id} mission={mission} />
                    ))}
                </Box>

                {/* Completed Missions */}
                <Box>
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                        <Trophy size={20} color={theme.palette.success.main} />
                        Abgeschlossene Missionen ({completedMissions.length})
                    </Typography>
                    {completedMissions.map((mission) => (
                        <CompletedMissionCard key={mission.id} mission={mission} />
                    ))}
                </Box>

                {/* Mission Hub Teaser */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        borderRadius: 2,
                        border: `1px dashed ${alpha(theme.palette.primary.main, 0.5)}`,
                    }}
                >
                    <Users size={20} color={theme.palette.primary.main} />
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                            +7 weitere Missionen verfügbar
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            Cloud Architecture • DevOps Mastery • UX Research • Agile Leadership
                        </Typography>
                    </Box>
                    <Button variant="outlined" color="primary" size="small" sx={{ textTransform: 'none' }}>
                        Mission Hub öffnen
                    </Button>
                </Box>
            </Stack>
        </Paper>
    );

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                        T4lo Missions
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        {gameEnabled
                            ? 'Sammle XP, schalte Badges frei und werde zum Skill-Champion!'
                            : 'T4lo Missions helfen dir, gezielt deine Skills auszubauen – für dich und dein Team.'}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Action Icons */}
                    <IconButton
                        size="small"
                        onClick={() => console.log('Mission Hub öffnen')}
                        aria-label="Mission hinzufügen"
                        title="Mission Hub öffnen"
                        sx={{
                            color: theme.palette.primary.main,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                            '&:hover': {
                                borderColor: theme.palette.primary.main,
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                            },
                        }}
                    >
                        <Plus size={16} />
                    </IconButton>

                    <IconButton
                        size="small"
                        onClick={() => console.log('Lernziel setzen')}
                        aria-label="Lernziel setzen"
                        title="Neues Lernziel setzen"
                        sx={{
                            color: theme.palette.primary.main,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                            '&:hover': {
                                borderColor: theme.palette.primary.main,
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                            },
                        }}
                    >
                        <Target size={16} />
                    </IconButton>

                    {/* View Toggle */}
                    {!isMobile && (
                        <Box
                            sx={{
                                display: 'flex',
                                border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                                borderRadius: 1,
                                ml: 1,
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={() => setViewMode('2-column')}
                                sx={{
                                    color:
                                        viewMode === '2-column'
                                            ? theme.palette.primary.main
                                            : theme.palette.text.secondary,
                                    bgcolor:
                                        viewMode === '2-column'
                                            ? alpha(theme.palette.primary.main, 0.1)
                                            : 'transparent',
                                    borderRadius: '4px 0 0 4px',
                                }}
                            >
                                <Columns2 size={16} />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => setViewMode('1-column')}
                                sx={{
                                    color:
                                        viewMode === '1-column'
                                            ? theme.palette.primary.main
                                            : theme.palette.text.secondary,
                                    bgcolor:
                                        viewMode === '1-column'
                                            ? alpha(theme.palette.primary.main, 0.1)
                                            : 'transparent',
                                    borderRadius: '0 4px 4px 0',
                                }}
                            >
                                <Rows2 size={16} />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Content Layout */}
            {currentLayout === '2-column' ? (
                <Box sx={{ display: 'flex', gap: 3 }}>
                    {/* Left Column */}
                    <Box sx={{ flex: '1 1 60%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <StatsCards />
                        <MissionsContent />
                    </Box>

                    {/* Right Column */}
                    <Box sx={{ flex: '1 1 40%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <T4loAssistantInfoBox context="missions" />
                    </Box>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <T4loAssistantInfoBox context="missions" />
                    <StatsCards />
                    <MissionsContent />
                </Box>
            )}
        </Box>
    );
}

export default T4loMissionsTab;
